
/* global ymaps3 */

import type { PropType } from 'vue'

import type {
    LngLatBounds, 
    YMap, 
    MapMode, 
    DomEventHandler, 
    YMapLocationRequest, 
    LngLat, 
} from '@yandex/ymaps3-types'

import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue'

import scriptManager from '@/modules/YandexMap3Module/scriptManager'

export default defineComponent({
    props: {
        id: {
            type: String,
            default: 'yandex-map'
        },
        scriptSrc: {
            type: String,
            required: true,
        },
        scriptId: {
            type: String,
            default: 'yandex-map-script'
        },
        mode: {
            type: String as PropType<MapMode>,
            default: 'vector',
        },
        initialMapCenter: {
            default: () => [76.92523545812777, 43.25391893213578],
        },
        initialMapZoom: {
            type: Number as PropType<number>,
            default: 12,
        },
        restrictMapArea: {
            type: [Object, Boolean] as PropType<LngLatBounds | false>,
            default: false,
        },
    },
    emits: ['script-load-error', 'script-loaded', 'map-ready', 'map-destroyed', 'map-clicked'],
    setup(props, { emit }) {
        const is_loading = ref(false)
        let map = null as YMap | null

        const onMapClick: DomEventHandler = (object, event) => {
            emit('map-clicked', { object, event })
        }

        const initializeMap = () => {
            const LOCATION: YMapLocationRequest = {
                center: props.initialMapCenter as LngLat,
                zoom: props.initialMapZoom,
            }

            const { YMap, YMapListener, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } = ymaps3

            map = new YMap(document.getElementById(props.id) as HTMLElement, {
                mode: props.mode,
                location: LOCATION,
                camera: {tilt: (45 * Math.PI) / 180},
                restrictMapArea: props.restrictMapArea,
            })

            map.addChild(new YMapDefaultSchemeLayer({}))
            map.addChild(new YMapDefaultFeaturesLayer({}))

            is_loading.value = false
            emit('map-ready', map)

            const mapListener = new YMapListener({
                layer: 'any',
                onFastClick: onMapClick,
            })

            map.addChild(mapListener)
        }

        const onScriptLoadError = () => {
            emit('script-load-error')
        }

        const onScriptLoaded = async () => {
            await ymaps3.ready
            initializeMap()
        }

        onMounted(() => {
            is_loading.value = true

            if (scriptManager.isScriptLoaded(props.scriptId)) {
                initializeMap()
                return
            }

            scriptManager.loadScript({
                scriptId: props.scriptId,
                scriptSrc: props.scriptSrc,
                onError: onScriptLoadError,
                onLoad: onScriptLoaded,
            })
        })

        onBeforeUnmount(() => {
            if (map) {
                map.destroy()
                map = null

                emit('map-destroyed')
            }
        })
    }
})
